// Export d'un tableau rendu : on lit les cellules telles qu'affichées

// Texte d'une cellule. Une formule KaTeX contient le rendu HTML et le MathML
// (textContent les répéterait) : elle est remplacée par sa source TeX.
function cellText(cell) {
  let node = cell;
  if (cell.querySelector('.katex')) {
    node = cell.cloneNode(true);
    node.querySelectorAll('.katex').forEach((katex) => {
      const tex = katex.querySelector('annotation[encoding="application/x-tex"]')?.textContent;
      katex.replaceWith(document.createTextNode(tex != null ? tex.trim() : katex.querySelector('.katex-html')?.textContent ?? ''));
    });
  }
  return node.textContent.replace(/\s+/g, ' ').trim();
}

/** Lignes du tableau : [[cellule, …], …], en-tête compris. */
export function readTable(table) {
  return Array.from(table.rows).map((row) => Array.from(row.cells).map(cellText));
}

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const SPACES_RE = /[\s\u00a0\u202f]/g;

// Cellule sans valeur (vide, tiret) : ignorée pour décider si une colonne est numérique
const isBlankCell = (text) => /^[-—–]?$/.test(String(text ?? '').trim());
// Code (« 007 », « 0123 », numéro de carte SIM à 19 chiffres) : un identifiant, jamais un
// nombre ; Excel en perdrait les zéros initiaux ou les chiffres au-delà du 15e
function isCode(text) {
  const clean = String(text ?? '').trim();
  return /^0\d+$/.test(clean.replace(SPACES_RE, '')) || /^\d{16,}$/.test(clean);
}

/** Nombre d'une cellule affichée (parseCellNumber), sauf code (zéro initial, plus de 15 chiffres) : null. */
export function cellNumber(text) {
  return isCode(text) ? null : parseCellNumber(text);
}

/**
 * Colonne numérique : toutes ses cellules remplies sont des nombres, et il y en a au
 * moins une. Cellules vides et tirets ignorés ; un code (« 007 ») la rend textuelle.
 */
export function isNumericColumn(cells) {
  let count = 0;
  for (const text of cells) {
    if (isBlankCell(text)) continue;
    if (cellNumber(text) === null) return false;
    count += 1;
  }
  return count > 0;
}

/**
 * Nombre et unité d'une cellule affichée : « 395 486 FCFA » → {value: 395486, unit: 'FCFA'},
 * « 9,8 L/100 km » → {value: 9.8, unit: 'L/100 km'}, « 12 % » → {value: 12, unit: '%'}.
 * null si la cellule n'est pas un nombre (codes compris).
 */
export function splitCellNumber(text) {
  const value = cellNumber(text);
  if (value === null) return null;
  const clean = String(text).replace(/[\u00a0\u202f]/g, ' ').trim();
  const per100 = /l\/100\s*km$/i.exec(clean);
  return { value, unit: per100 ? per100[0] : clean.replace(/^[\s\S]*\d/, '').trim() };
}

// Unités comparées sans casse ni espaces (« L/100 km » = « l/100km »)
const unitKey = (unit) => unit.toLowerCase().replace(SPACES_RE, '');

// Colonne exportée en nombres bruts : {unit, values} si toutes ses cellules remplies sont
// des nombres de même unité, sinon null (texte affiché conservé). `numbers` : nombres
// bruts déjà connus (onglet « Données » d'un visuel), alignés sur `cells`.
function rawColumn(cells, numbers) {
  let unit = null;
  const values = cells.map((text, i) => {
    if (unit === false || isBlankCell(text)) return null;
    const split = splitCellNumber(String(text ?? ''));
    const value = isNum(numbers?.[i]) ? numbers[i] : split?.value ?? null;
    if (value === null || !split) {
      unit = false;
      return null;
    }
    if (unit === null) unit = split.unit;
    else if (unitKey(unit) !== unitKey(split.unit)) unit = false;
    return value;
  });
  return typeof unit === 'string' ? { unit, values } : null;
}

// Unité reportée dans l'en-tête, sauf si elle y figure déjà comme mot (« Coût (FCFA) »,
// « Conso L/100 km ») ; « Charge » ne contient pas l'unité « h »
function headerWithUnit(label, unit) {
  if (!unit || unit === '%') return label;
  const pattern = [...unitKey(unit)].map((ch) => ch.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')).join('\\s*');
  const text = String(label ?? '').toLowerCase().replace(/[\u00a0\u202f]/g, ' ');
  if (new RegExp(`(^|[^\\p{L}])${pattern}($|[^\\p{L}])`, 'u').test(text)) return label;
  return label ? `${label} (${unit})` : unit;
}

// Cellule qu'Excel lirait comme une formule (« =… », « @… », « +x » non numérique) :
// préfixée d'une apostrophe. Les nombres signés (« -3 », « +8,5 % ») restent des nombres.
const FORMULA_START_RE = /^[=@\t\r+-]/;
// Lecture stricte (unités connues seulement) : « +1 cmd » reste neutralisé
const SAFE_NUMBER_RE = /^[-+]?\d+(?:[.,]\d+)?(?:%|fcfa|xof|eur|€|\$|km|l\/100km|l|h|j)?$/i;
const neutralize = (c) => (FORMULA_START_RE.test(c) && !SAFE_NUMBER_RE.test(c.replace(/[\s\u00a0\u202f]/g, '')) ? `'${c}` : c);

// Nombre brut à la française : virgule décimale, ni séparateur de milliers ni unité
const rawNumber = (n) => String(n).replace('.', ',');
// Texte collé dans Excel : un code reste du texte (« '007 », zéros conservés), comme tout
// identifiant numérique d'une colonne de codes ; une cellule lue comme une formule est
// neutralisée. Excel n'affiche pas l'apostrophe.
const excelText = (text, codeColumn = false) => {
  const cell = String(text ?? '').replace(/[\t\r\n]+/g, ' ');
  return isCode(cell) || (codeColumn && /^\d+$/.test(cell)) ? `'${cell}` : neutralize(cell);
};

/**
 * TSV « Pour Excel » : chaque cellule garde sa colonne une fois collée. Une colonne de
 * nombres de même unité est copiée en nombres bruts (« 395486 », « 482,3 »), l'unité
 * passant dans l'en-tête (« Coût (FCFA) ») ; « % » reste sur chaque valeur (« 12,5% »,
 * lu comme un pourcentage). Unités mélangées et texte : tels qu'affichés ; codes forcés
 * en texte (« '007 »). `values` (facultatif) : nombres bruts alignés sur les lignes hors
 * en-tête (payload.values du panneau), préférés au texte affiché (arrondi).
 */
export function toTSV(rows, { values = null } = {}) {
  if (!rows?.length) return '';
  const [header, ...body] = rows;
  const width = Math.max(...rows.map((r) => r.length));
  const columns = Array.from({ length: width }, (_, col) =>
    rawColumn(body.map((r) => r[col] ?? ''), values ? body.map((_, i) => values[i]?.[col]) : null));
  const codeColumns = Array.from({ length: width }, (_, col) => body.some((r) => isCode(r[col])));
  const exportCell = (text, col, i) => {
    const n = columns[col]?.values[i];
    if (!isNum(n)) return excelText(text, codeColumns[col]);
    return columns[col].unit === '%' ? `${rawNumber(n)}%` : rawNumber(n);
  };
  const lines = [
    header.map((label, col) => excelText(headerWithUnit(label, columns[col]?.unit))),
    ...body.map((cells, i) => cells.map((text, col) => exportCell(text, col, i))),
  ];
  return lines.map((cells) => cells.join('\t')).join('\n');
}

// Séparateur « ; » pour un fichier ouvert dans Excel en français (la virgule y est décimale).
// Guillemets seulement si la cellule contient le séparateur, un guillemet ou un saut de
// ligne : « 8,7 » reste un nombre décimal non entouré de guillemets.
export function toCSV(rows, separator = ',') {
  const mustQuote = (c) => c.includes(separator) || /["\r\n]/.test(c);
  const escape = (raw) => {
    const c = neutralize(raw);
    return mustQuote(c) ? `"${c.replace(/"/g, '""')}"` : c;
  };
  return rows.map((cells) => cells.map(escape).join(separator)).join('\n');
}

// Valeur numérique d'une cellule affichée « 4 210 », « 482,3 », « 12 % », « 395 486 FCFA »,
// « 1 234,5 FCFA/km », « 12 L/h » : toute unité finale faite de lettres, « / », %, € ou $
const NUMBER_RE = /^[-+]?\d+(?:\.\d+)?$/;
export function parseCellNumber(text) {
  if (typeof text !== 'string') return null;
  const clean = text
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(/l\/100km$/i, '')
    .replace(/[\p{L}/%€$°]+$/u, '')
    .replace('\u2212', '-')
    .replace(',', '.');
  return NUMBER_RE.test(clean) ? Number(clean) : null;
}

export function toMarkdownTable(rows) {
  if (!rows.length) return '';
  const width = Math.max(...rows.map((r) => r.length));
  const pad = (cells) => Array.from({ length: width }, (_, i) => (cells[i] ?? '').replace(/\|/g, '\\|'));
  const line = (cells) => `| ${pad(cells).join(' | ')} |`;
  const [header, ...body] = rows;
  return [line(header), `| ${Array(width).fill('---').join(' | ')} |`, ...body.map(line)].join('\n');
}
